import React from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { CountsByPriceV2, ProgressRing } from '../../components';
import { MetricLabel, PriceRangeContainer, PriceRangeGrid, PriceRangeItem, PriceRangeText, StyledDivider, StyledSkeletonBox } from '../../RegistryGiftAdvisor.styles';
import { Page } from '../../telemetry';
import { CalculatedPriceRangeFragment } from '@graphql/generated';

interface PriceRangeTitleAndLink {
  title: string;
  currentCount: number;
  priceRangeQuery: string;
  telemetryLabel: string;
}

interface AdvisorPriceRangesProps {
  getResultsLoading?: boolean;
  giftPriceBalance: () => string;
  priceRanges?: CalculatedPriceRangeFragment[];
  getPriceRangeTitleAndLink: (priceRange: CalculatedPriceRangeFragment) => PriceRangeTitleAndLink;
  isTabletScreen?: boolean;
  page: Page;
  isMobile?: boolean;
  targetGiftCount?: number;
  shopNow?: () => string;
  onPriceRangeClick?: (title: string, priceRangeQuery: string) => void;
}

const RING_DIMENSIONS = {
  width: 52,
  height: 52,
  cx: 26,
  cy: 26,
  r: 23,
  strokeWidth: 4.65,
};

const AdvisorPriceRanges: React.FC<AdvisorPriceRangesProps> = ({
  giftPriceBalance,
  getPriceRangeTitleAndLink,
  priceRanges,
  isTabletScreen = false,
  page,
  getResultsLoading,
  isMobile = false,
  targetGiftCount,
  shopNow,
  onPriceRangeClick,
}) => {
  if (isMobile) {
    return (
      <PriceRangeContainer padding={6}>
        <PriceRangeText marginBottom={4}>{giftPriceBalance()}</PriceRangeText>
        <PriceRangeGrid>
          {getResultsLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <PriceRangeItem
                  key={i}
                  marginBottom={4}
                >
                  <StyledSkeletonBox
                    width="52px"
                    height="52px"
                    borderRadius="50%"
                    marginBottom={2}
                  />
                  <StyledSkeletonBox
                    width="80px"
                    height="16px"
                  />
                </PriceRangeItem>
              ))
            : priceRanges?.map(range => {
                const { title, currentCount, priceRangeQuery } = getPriceRangeTitleAndLink(range);
                return (
                  <PriceRangeItem
                    key={title}
                    marginBottom={6}
                  >
                    <ProgressRing
                      progressPercent={Math.round((currentCount / targetGiftCount!) * 100)}
                      isOpen
                      showCheck
                      dimensions={RING_DIMENSIONS}
                      currentGiftCount={currentCount}
                      countTextColor="#595959"
                      progressColor="#595959"
                      typographyVariant="hed1"
                    />
                    <Flex flexDirection="column">
                      <MetricLabel
                        typographyVariant="body2"
                        marginTop={3}
                      >
                        {title}
                      </MetricLabel>
                      <ButtonV2
                        onClick={() => onPriceRangeClick?.(title, priceRangeQuery)}
                        marginTop={pxToRem(2)}
                        variant="ghost"
                        padding={0}
                        height="fit-content"
                        color="mono10"
                        typographyVariant="label2"
                      >
                        {shopNow?.()}
                      </ButtonV2>
                    </Flex>
                  </PriceRangeItem>
                );
              })}
        </PriceRangeGrid>
      </PriceRangeContainer>
    );
  }

  return (
    <>
      <StyledDivider />
      <Box
        padding={6}
        display={'flex'}
        flexDirection={isTabletScreen ? 'column' : 'row'}
        width="100%"
      >
        <TextV2
          fontSize={pxToRem(15)}
          color="mono12"
          width={isTabletScreen ? '100%' : '200px'}
          alignItems="center"
          justifyContent={isTabletScreen ? 'center' : ''}
          display="flex"
          fontWeight={500}
          marginBottom={isTabletScreen ? 4 : ''}
        >
          {giftPriceBalance()}
        </TextV2>
        <Box
          display="flex"
          justifyContent="space-between"
          marginTop={isTabletScreen ? 4 : 0}
          width="100%"
        >
          <CountsByPriceV2
            isOpen
            getPriceRangeTitleAndLink={getPriceRangeTitleAndLink}
            priceRanges={priceRanges}
            page={page}
            wrapItems={false}
          />
        </Box>
      </Box>
    </>
  );
};

export default AdvisorPriceRanges;
